import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, History } from 'lucide-react';
import { Layout } from '../layout/Layout';
import EditorCard from './EditorCard';
import { InsightsPanel } from './InsightsPanel';
import { CorrectionsBottomSheet } from './CorrectionsBottomSheet';
import TamilEditor from './TamilEditor';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ROUTES } from '../../utils/constants';
import type { UISuggestion, GrammarRubric } from '../../types/grammar';

export type TamilGrammarAssistantPageProps = {
  editorContent: string;
  onEditorContentChange: (value: string) => void;
  suggestions: UISuggestion[];
  rubric: GrammarRubric | null;
  correctedText: string | null;
  focusedId: string | null;
  onFocusSuggestion: (id: string | null) => void;
  onCheckGrammar: (text: string) => Promise<void>;
  onUploadFile: (file: File) => Promise<void>;
  onUploadOcrOnly: (file: File) => Promise<void>;
  loadingOcrOnly?: boolean;
  onClear: () => void;
  isLoading?: boolean;
  onBackToPractice?: () => void;
  /** Apply a single correction to editor text (replace original with suggestion). Used by mobile bottom sheet Accept. */
  onApplySuggestion?: (suggestion: UISuggestion) => void;
};

const wordCount = (text: string) =>
  text.trim() ? text.trim().split(/\s+/).filter(Boolean).length : 0;

export const TamilGrammarAssistantPage: React.FC<TamilGrammarAssistantPageProps> = ({
  editorContent,
  onEditorContentChange,
  suggestions,
  rubric,
  correctedText,
  focusedId,
  onFocusSuggestion,
  onCheckGrammar,
  onUploadFile,
  onUploadOcrOnly,
  loadingOcrOnly = false,
  onClear,
  isLoading = false,
  onBackToPractice,
  onApplySuggestion,
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetActiveIndex, setSheetActiveIndex] = useState(0);

  const handleCopyCorrected = () => {
    if (correctedText) {
      navigator.clipboard.writeText(correctedText);
    }
  };

  const openCorrectionsSheet = () => {
    if (suggestions.length > 0) {
      setSheetActiveIndex(0);
      setSheetOpen(true);
    }
  };

  const handleSheetPrev = () => {
    setSheetActiveIndex((i) => Math.max(0, i - 1));
  };
  const handleSheetNext = () => {
    if (sheetActiveIndex >= suggestions.length - 1) {
      setSheetOpen(false);
    } else {
      setSheetActiveIndex((i) => i + 1);
    }
  };
  const handleSheetAccept = (suggestion: UISuggestion) => {
    onApplySuggestion?.(suggestion);
    if (sheetActiveIndex >= suggestions.length - 1) {
      setSheetOpen(false);
    } else {
      setSheetActiveIndex((i) => i + 1);
    }
  };
  const handleSheetSkip = () => {
    handleSheetNext();
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-88px)] overflow-hidden flex flex-col bg-slate-50 dark:bg-slate-950 pb-24 sm:pb-0">
        <div className="px-4 sm:px-6 py-4 sm:py-6 flex-shrink-0">
          {/* Top row: H1 + Track + Clear — stack on mobile */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight text-slate-900 dark:text-slate-100">
              Tamil Grammar Assistant
            </h1>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => navigate(ROUTES.STUDENT_WRITING_HISTORY)}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 h-9 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
                aria-label="Open writing check history"
              >
                <History className="w-4 h-4" />
                <span className="text-sm">Track</span>
              </button>
              <button
                type="button"
                onClick={onClear}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 h-9 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
                aria-label="Clear editor and results"
              >
                <Trash2 className="w-4 h-4" />
                <span className="text-sm">Clear</span>
              </button>
            </div>
          </div>
        </div>

        {/* Grid: Editor (8) + Insights (4) — right panel hidden on mobile */}
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-6 px-4 sm:px-6 pb-4 sm:pb-6">
          <div className="lg:col-span-8 h-full overflow-hidden min-w-0">
            <TamilEditor
              content={editorContent}
              onContentChange={onEditorContentChange}
              suggestions={suggestions}
              focusedId={focusedId}
              isLoading={isLoading}
              showCorrectedPanel={false}
              onCheckGrammar={onCheckGrammar}
              onUploadFile={onUploadFile}
              renderEmbed={({ toolbarLeft, editorContent: editorSlot }) => (
                <EditorCard
                  errorCount={suggestions.length}
                  correctedText={correctedText}
                  wordCount={wordCount(editorContent)}
                  isLoading={isLoading}
                  toolbarLeft={toolbarLeft}
                  editorSlot={editorSlot}
                  onCheckGrammar={async () => {
                    const text = editorContent;
                    if (text.trim()) await onCheckGrammar(text);
                  }}
                  onUpload={async () => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.pdf,image/*';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) onUploadFile(file);
                    };
                    input.click();
                  }}
                  loadingOcrOnly={loadingOcrOnly}
                  onUploadOcr={async () => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) onUploadOcrOnly(file);
                    };
                    input.click();
                  }}
                  onCopyCorrected={handleCopyCorrected}
                />
              )}
            />
          </div>
          {/* Desktop/tablet: right panel (scorecard + corrections list) */}
          <div className="hidden lg:block lg:col-span-4 h-full overflow-hidden min-w-0">
            <InsightsPanel
              rubric={rubric}
              suggestions={suggestions}
              onHover={onFocusSuggestion}
              isChecking={isLoading}
            />
          </div>
        </div>

        {/* Mobile-only: fixed bottom action bar (word count + Correct All) */}
        {isMobile && (
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-900/95 border-t border-slate-200 dark:border-slate-800 px-4 py-3 flex items-center justify-between sm:hidden">
            <div className="text-sm px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {wordCount(editorContent)} {wordCount(editorContent) === 1 ? 'word' : 'words'}
            </div>
            <button
              type="button"
              onClick={openCorrectionsSheet}
              disabled={suggestions.length === 0}
              className="relative rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:pointer-events-none text-white px-5 py-2.5 font-semibold text-sm transition-colors"
            >
              Correct All
              {suggestions.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                  {suggestions.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Mobile-only: corrections bottom sheet */}
        {isMobile && (
          <CorrectionsBottomSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            suggestions={suggestions}
            activeIndex={sheetActiveIndex}
            onPrev={handleSheetPrev}
            onNext={handleSheetNext}
            onAccept={handleSheetAccept}
            onSkip={handleSheetSkip}
          />
        )}
      </div>
    </Layout>
  );
};

export default TamilGrammarAssistantPage;
